import { CompanyType } from 'types/join';
import { getCookie } from './setCookies';

const RAPAPORT_USER_DATA_COOKIE = 'rapaport_user_data';

/** The cookie stores utm values as either a plain string or an array. */
type CookieUtmValue = string | string[];

/**
 * Shape of the `rapaport_user_data` cookie dropped by the pre-registration
 * site. Only a subset of these fields is consumed by the registration form;
 * the rest (recordtypeid, leadsource, etc.) are intentionally ignored.
 */
export interface RapaportUserData {
  firstname?: string;
  lastname?: string;
  email?: string;
  phone?: string;
  company?: string;
  country?: string;
  recordtypeid?: string[];
  product_interests?: string[];
  leadsource?: string[];
  buyer_or_seller?: string;
  hubspot_company_type?: string[];
  preferred_plan?: string;
  environment?: string;
  utm_campaign_temp?: CookieUtmValue;
  utm_campaign?: CookieUtmValue;
  utm_source_temp?: CookieUtmValue;
  utm_source?: CookieUtmValue;
  utm_medium_temp?: CookieUtmValue;
  utm_medium?: CookieUtmValue;
  utm_term__temp_?: CookieUtmValue;
  utm_term?: CookieUtmValue;
  utm_content__temp_?: CookieUtmValue;
  utm_content?: CookieUtmValue;
}

const tryParse = (value: string): RapaportUserData | null => {
  try {
    return JSON.parse(value) as RapaportUserData;
  } catch {
    return null;
  }
};

/**
 * Reads and parses the `rapaport_user_data` cookie. The cookie value is JSON
 * and likely percent-encoded (it contains spaces, e.g. "United States"), so we
 * attempt to decode before parsing and fall back to the raw value. Returns
 * `null` when the cookie is missing or cannot be parsed.
 */
export const getRapaportUserData = (): RapaportUserData | null => {
  const raw = getCookie(RAPAPORT_USER_DATA_COOKIE);
  if (!raw) return null;

  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    decoded = raw;
  }

  return tryParse(decoded) ?? tryParse(raw);
};

/**
 * Environment guard: only consume the cookie when its `environment` matches the
 * app's `CONFIG.__ENV` (case-insensitive). A missing `environment` is allowed
 * so older cookies do not regress.
 */
export const isUserDataForCurrentEnv = (userData: RapaportUserData): boolean => {
  const env = userData.environment;
  if (!env) return true;

  const currentEnv = typeof CONFIG !== 'undefined' ? CONFIG.__ENV : '';
  return String(env).toLowerCase() === String(currentEnv ?? '').toLowerCase();
};

const utmValueToString = (value?: CookieUtmValue): string => {
  if (Array.isArray(value)) return value.find(item => Boolean(item)) ?? '';
  return value ?? '';
};

export interface CookieUtmParams {
  utmCampaign: string;
  utmSource: string;
  utmMedium: string;
  utmTerm: string;
  utmContent: string;
}

/**
 * UTM parameters captured by the pre-registration site, read from the
 * `rapaport_user_data` cookie. The `*_temp` keys are the ones the
 * pre-registration HubSpot form actually populates, so they win over the
 * plain keys. Every value normalizes to '' when absent.
 */
export const getCookieUtmParams = (): CookieUtmParams => {
  const userData = getRapaportUserData();
  return {
    utmCampaign:
      utmValueToString(userData?.utm_campaign_temp) || utmValueToString(userData?.utm_campaign),
    utmSource:
      utmValueToString(userData?.utm_source_temp) || utmValueToString(userData?.utm_source),
    utmMedium:
      utmValueToString(userData?.utm_medium_temp) || utmValueToString(userData?.utm_medium),
    utmTerm: utmValueToString(userData?.utm_term__temp_) || utmValueToString(userData?.utm_term),
    utmContent:
      utmValueToString(userData?.utm_content__temp_) || utmValueToString(userData?.utm_content)
  };
};

const normalizeCompanyType = (value: string) => (value ?? '').toLowerCase().replace(/[_/\s]/g, '');

/**
 * Best-effort match of the cookie's company type against the CompanyTypes
 * dropdown options. Returns the matching option `name`, or '' when none match.
 */
export const matchCompanyType = (cookieType: string, companyTypes: CompanyType[]): string => {
  if (!cookieType || !companyTypes?.length) return '';

  const target = normalizeCompanyType(cookieType);
  const match = companyTypes.find(type => normalizeCompanyType(type.name) === target);
  return match?.name ?? '';
};

/**
 * Deletes the `rapaport_user_data` cookie after registration completes so it
 * cannot prefill (or suppress the HubSpot submission for) a later, unrelated
 * registration on the same machine.
 */
export const deleteRapaportUserData = (): void => {
  document.cookie = `${RAPAPORT_USER_DATA_COOKIE}=; expires=${new Date(0).toUTCString()}; path=/;`;
};
